import { ParamMap } from '@angular/router';
import { Product } from '@domain/ProductAndServices/Product';
import { UserRegistration } from '@domain/UserRegistration';
import { Cobro } from './cobro';

/**
 * Que tramite recorren las pantallas de sede, horario y pago (SPEC-016).
 *
 * **Existe porque una renovacion no tiene `Application`.** Las tres pantallas se
 * construyeron sobre la solicitud —`getApplicationById`, y de ahi el curso, el
 * nivel, los productos y lo elegido— y el bloque 6 se comparte entre los dos
 * tramites. Esto es ese contrato, con dos implementaciones: `TramiteDeSolicitud`
 * lo arma con la solicitud, como hasta hoy, y `TramiteDeRenovacion` con el nivel
 * que el servidor deja habilitado.
 *
 * **No parametriza la oferta**, y conviene decirlo porque es lo primero que se
 * intenta: las sedes y los horarios salen de `CatalogoService.oferta(courseId,
 * levelId, seasonId)` sobre los grupos activos, y eso es **igual en los dos
 * tramites**. Lo que cambia es de donde salen el curso y el nivel, a donde va
 * cada pantalla, y **que se escribe al final** — que es lo mas distinto de todo.
 */
export interface Tramite {
  /** Con que se pide la oferta. En renovacion no es elegible: lo dice el servidor. */
  readonly courseId: string;
  readonly levelId: string;

  /** El nombre del nivel, tal y como lo escribe el catalogo. Va en el subtitulo. */
  readonly nivel: string | null;

  /**
   * Los productos que le tocan, ya en su rama y ya ordenados (AC-3).
   *
   * `newRegister` en matricula nueva y `renewal` en renovacion. La rama la elige
   * quien construye el tramite, que es el unico que sabe cual es; la pantalla de
   * pago cobra lo que hay aqui y no vuelve a preguntar de que tramite se trata.
   */
  readonly productos: readonly Product[];

  /**
   * La sede y el horario ya elegidos, o `null` si todavia no hay eleccion.
   *
   * **Los dos tramites llegan aqui por caminos distintos.** En matricula nueva
   * esta guardado en la solicitud desde que se eligio, y por eso se puede volver
   * por el enlace del correo y encontrarlo puesto. En renovacion **no hay donde
   * guardarlo** hasta que exista el pago, asi que viaja en la URL y se
   * reconstruye contra el catalogo. Lo que no se hereda nunca es un intento
   * anterior: una renovacion a medias no se retoma, se empieza de nuevo
   * (usuario, 2026-09-08).
   */
  readonly elegido: UserRegistration | null;

  /**
   * Los ciclos en los que el alumno **ya esta matriculado**, para no ofrecerlos.
   *
   * **Una matricula por ciclo**: la Escuela no permite cursar dos niveles en el
   * mismo ciclo (usuario, 2026-09-08). Lo encontro lab — un Nivel 4 reprobado en
   * el CICLO 3 veia ofrecido ese mismo CICLO 3 para repetirlo—.
   *
   * **Vacio en matricula nueva**: no hay historial que mirar, y el nivel lo acaba de
   * asignar el gestor.
   */
  readonly ciclosMatriculados: readonly string[];

  /**
   * Lo que la pantalla de pago tiene que advertir en **este** tramite, si algo.
   *
   * Nace de los 20 minutos de la Logic App de renovaciones: quien tarde mas en
   * pagar pierde la renovacion y empieza de cero. **En matricula nueva no
   * aplica** —alli el pago puede esperar dias— y copiar la advertencia sin mirar
   * seria asustar sin motivo, igual que callarla deja a la otra persona sin aviso.
   */
  readonly avisoDePago: string | null;
}

/**
 * A donde sigue el recorrido. Son los mismos cuatro pasos en los dos tramites
 * y **las direcciones no se parecen en nada**: una cuelga del id de la solicitud
 * y la otra del alumno, con lo elegido dentro.
 *
 * Se resuelven **sin esperar a nada** —salen de la ruta, que ya esta— porque la
 * pantalla necesita saber a donde vuelve desde el primer pintado, incluido el
 * del esqueleto de carga.
 */
export interface EnlacesDelTramite {
  /** De donde se llega a la primera pantalla: el estado del tramite, o la busqueda. */
  readonly atras: string[];
  readonly sede: string[];
  horario(ciclo: string, sede: string): string[];
  pago(ciclo: string, grupo: string): string[];
}

/**
 * No se pudo traer el tramite, y por eso no hay ni curso ni nivel que consultar.
 *
 * **Se distingue de que falle el catalogo**, que es el otro fallo posible de
 * estas pantallas: en un caso no se sabe ni de quien es el tramite, y en el otro
 * el tramite esta a la vista y lo que falta es la oferta. Son dos mensajes
 * distintos y esta clase es lo que permite elegir cual.
 */
export class SinTramite extends Error {}

/**
 * De donde sale el tramite, y **donde acaba lo que se elige**.
 *
 * Lo lee todo de la ruta: en matricula nueva el `id` de la solicitud y en
 * renovacion el `miembro` con lo elegido detras. Que la ruta sea la fuente es lo
 * que permite que una recarga a mitad del recorrido siga funcionando (Regla 4)
 * sin un store por debajo — que ademas no podria ser `MatriculaStore`, porque
 * estas pantallas viven fuera del caparazon del formulario a proposito.
 */
export interface OrigenDelTramite {
  /** A donde va cada pantalla, leido de la ruta y sin consultar nada. */
  enlaces(params: ParamMap): EnlacesDelTramite;

  /** El tramite, o `SinTramite` si no se pudo traer. */
  traer(params: ParamMap): Promise<Tramite>;

  /**
   * Guardar la eleccion de sede y horario, **si este tramite la guarda**.
   *
   * Una matricula nueva la escribe en `userRegistration` de la solicitud. Una
   * renovacion no escribe nada aqui: su membresia nace con el pago, y hasta
   * entonces la eleccion viaja en la URL.
   */
  guardar(params: ParamMap, inscripcion: UserRegistration): Promise<void>;

  /**
   * Crear el pago y salir al checkout. Lanza `SinCupo` si el grupo se lleno
   * mientras tanto, y un error cualquiera si no se pudo crear.
   */
  pagar(params: ParamMap, tramite: Tramite, cobro: Cobro): Promise<void>;
}
